// ============================================================
// WebsiteView — the "Sito web" tab. Two-pane editor: a sticky section nav
// (scroll-spy) on the left, the content cards on the right. Lets the owner
// edit their public website's CONTENT — never its design/structure (locked
// in the repo config). Debounced auto-save to draft; "Pubblica" promotes it.
// Photos upload to Spaces (downscaled client-side) when configured; otherwise
// owners paste image URLs.
// ============================================================
import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import './WebsiteView.css';
import type { Lang } from '../data/types';
import { useDashboard, useDashboardActions } from '../data/context';
import { downscaleImage } from '../lib/image';
import { setByPath } from '../lib/path';
import type { SiteContent, SiteMenuTag, SiteWeekday } from '@risma/data-client';

// Editing INLINE: converte il testo arrivato dall'anteprima nel valore del campo.
// Prezzi/numeri: parse IT (togli valuta/migliaia, virgola→punto). Vuoto → undefined
// (campo opzionale). Non parsabile → SKIP: non si tocca la bozza (al blur il valore
// formattato torna da solo col render). Testo: trim, vuoto → undefined.
const SKIP = Symbol('skip');
function coerce(value: unknown, kind?: string): string | number | undefined | typeof SKIP {
  if (kind === 'price' || kind === 'number') {
    const raw = String(value ?? '').trim();
    if (raw === '') return undefined;
    const cleaned = raw.replace(/[^\d.,-]/g, '').replace(/\.(?=\d{3}(?:\D|$))/g, '').replace(',', '.');
    if (cleaned === '' || cleaned === '-') return SKIP; // testo senza cifre (es. "abc") → non toccare la bozza
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : SKIP;
  }
  const s = String(value ?? '').trim();
  return s === '' ? undefined : s;
}

interface Props {
  lang: Lang;
}

const DAYS: { key: SiteWeekday; label: string }[] = [
  { key: 'mon', label: 'Lunedì' },
  { key: 'tue', label: 'Martedì' },
  { key: 'wed', label: 'Mercoledì' },
  { key: 'thu', label: 'Giovedì' },
  { key: 'fri', label: 'Venerdì' },
  { key: 'sat', label: 'Sabato' },
  { key: 'sun', label: 'Domenica' },
];

const TAGS: { key: SiteMenuTag; label: string }[] = [
  { key: 'vegetariano', label: 'Vegetariano' },
  { key: 'vegano', label: 'Vegano' },
  { key: 'piccante', label: 'Piccante' },
  { key: 'senza-glutine', label: 'Senza glutine' },
  { key: 'senza-lattosio', label: 'Senza lattosio' },
  { key: 'specialita', label: 'Specialità' },
  { key: 'novita', label: 'Novità' },
  { key: 'crudo', label: 'Crudo' },
];

const SECTIONS: { id: string; label: string; icon: SecName }[] = [
  { id: 'hero', label: 'Testata', icon: 'star' },
  { id: 'identita', label: 'Identità', icon: 'id' },
  { id: 'racconto', label: 'Racconto', icon: 'book' },
  { id: 'menu', label: 'Menù', icon: 'grid' },
  { id: 'orari', label: 'Orari', icon: 'clock' },
  { id: 'galleria', label: 'Galleria', icon: 'image' },
  { id: 'recensioni', label: 'Recensioni', icon: 'message' },
  { id: 'contatti', label: 'Contatti', icon: 'mail' },
  { id: 'social', label: 'Social', icon: 'share' },
  { id: 'seo', label: 'Google & SEO', icon: 'search' },
  { id: 'lingue', label: 'Lingue', icon: 'globe' },
];

// Sezioni dell'editor NON presenti nello scheletro di un template → nascoste per
// quel template (l'editor non deve mostrare cose che il sito non userebbe). Le
// sezioni-firma di Lampo (manifesto, marquee…) sono design, non si editano qui.
const TEMPLATE_HIDDEN: Record<string, string[]> = {
  // 'lingue' (bilingue IT/EN) è cablato solo sul Classico per ora → nascosto altrove.
  lampo: ['racconto', 'recensioni', 'lingue'],
  // Carta usa il racconto (banda scura editoriale) ma non ha sezione recensioni.
  carta: ['recensioni', 'lingue'],
  // Marea usa il racconto (Chi siamo) ma non ha sezione recensioni.
  marea: ['recensioni', 'lingue'],
};

type Status = 'loading' | 'saved' | 'dirty' | 'saving' | 'error' | 'publishing' | 'published';

export function WebsiteView(_props: Props) {
  const actions = useDashboardActions();
  const { isImpersonated, tenant } = useDashboard();
  // Sezioni da nascondere per il template del cliente (vedi TEMPLATE_HIDDEN).
  const hiddenSecs = new Set(TEMPLATE_HIDDEN[tenant?.template ?? 'classico'] ?? []);
  // Etichette/aiuti dell'editor cambiano col template: lo stesso campo su Lampo
  // fa un'altra cosa (es. la "Frase sotto al nome" è il CLAIM del manifesto).
  const isLampo = (tenant?.template ?? 'classico') === 'lampo';
  const isCarta = (tenant?.template ?? 'classico') === 'carta';
  const isMarea = (tenant?.template ?? 'classico') === 'marea';
  // Classico con "foto testata che si alternano" attivo (Console) → sblocca il
  // caricamento multi-foto dell'hero (hero.images), come per Lampo.
  const heroSlideshow =
    !isLampo && !isCarta && (tenant?.templateConfig as { heroSlideshow?: boolean } | undefined)?.heroSlideshow === true;
  const [content, setContent] = useState<SiteContent>({});
  const [status, setStatus] = useState<Status>('loading');
  const [active, setActive] = useState<string>('hero');
  const [published, setPublished] = useState(false);
  const [uploadsEnabled, setUploadsEnabled] = useState(false);
  // Traduzione automatica (sezione "Lingue"): stato del bottone + esito.
  const [translating, setTranslating] = useState(false);
  const [translateMsg, setTranslateMsg] = useState<{ ok: boolean; text: string } | null>(null);
  // Vero DURANTE la traduzione: mette in pausa l'autosave, così un salvataggio in
  // volo (senza locales.en) non può sovrascrivere l'inglese appena generato.
  const translatingRef = useRef(false);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  /** Downscale then upload a photo; resolves to its public URL. */
  async function uploadPhoto(file: File): Promise<string> {
    const blob = await downscaleImage(file);
    return actions.uploadImage(blob);
  }

  // ── live preview ──
  // In impersonazione il portale è quello OSPITE (Carlotta): l'anteprima parte
  // dal suo sito. Chiediamo la modalità NEUTRA (?neutral=1, contenuti vuoti) +
  // lo STILE del cliente (carattere/colori/hero/numeri scelti in Console), così
  // l'anteprima rispecchia il look reale e non quello di default di Carlotta.
  const siteBase = import.meta.env.VITE_SITE_URL;
  const previewUrl = (() => {
    if (!siteBase) return null;
    const p = new URLSearchParams();
    if (isImpersonated) p.set('neutral', '1');
    // Il TEMPLATE (scheletro) prima di tutto: anche in impersonazione (base =
    // sito ospite Carlotta, classico) l'anteprima deve disegnare il template del
    // cliente, non il Classico. preview.astro legge ?template e lo forza.
    if (tenant?.template && tenant.template !== 'classico') p.set('template', tenant.template);
    // Testi-firma del template (operatore) → l'anteprima mostra i testi veri anche
    // in impersonazione (dove la base è il sito ospite), non i default.
    if (tenant?.templateConfig && Object.keys(tenant.templateConfig).length) {
      p.set('tcfg', JSON.stringify(tenant.templateConfig));
    }
    if (tenant?.designStyle) p.set('style', tenant.designStyle);
    if (tenant?.heroLayout) p.set('hero', tenant.heroLayout);
    if (tenant?.sectionNumerals != null) p.set('numerals', tenant.sectionNumerals ? 'on' : 'off');
    const qs = p.toString();
    return `${siteBase.replace(/\/$/, '')}/preview${qs ? `?${qs}` : ''}`;
  })();
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const contentRef = useRef<SiteContent>({});
  const previewTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Full-tab previews opened via "Apri ↗". We keep their window handles so
  // they receive the same live updates as the embedded iframe.
  const popupRefs = useRef<Set<Window>>(new Set());
  // Editing inline: la finestra che sta scrivendo (anti-eco) + timer che la "scorda".
  const editingSourceRef = useRef<Window | null>(null);
  const editingClearTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  function postTo(target: Window | null | undefined, c: SiteContent) {
    if (!siteBase || !target) return;
    try {
      // `name` non è contenuto editabile (sta sul tenant, non in SiteContent):
      // lo mandiamo a parte così l'anteprima — anche quella neutra — mostra il
      // nome VERO del locale invece del segnaposto.
      target.postMessage({ source: 'risma-editor', content: c, name: tenant?.name }, siteBase);
    } catch {
      /* window gone */
    }
  }
  function postPreview(c: SiteContent, except?: Window | null) {
    if (!siteBase) return;
    // Anti-eco: NON rimando il contenuto alla finestra che ha originato la modifica
    // inline (sta scrivendo lì, ha già il testo giusto a schermo; ridipingerla
    // sposterebbe il cursore). Le altre superfici si aggiornano normalmente.
    const iframeWin = iframeRef.current?.contentWindow;
    if (iframeWin && iframeWin !== except) postTo(iframeWin, c);
    for (const w of popupRefs.current) {
      if (w.closed) popupRefs.current.delete(w);
      else if (w !== except) postTo(w, c);
    }
  }
  function schedulePreview(c: SiteContent) {
    contentRef.current = c;
    if (previewTimer.current) clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(() => postPreview(c, editingSourceRef.current), 180);
  }
  function openFullPreview() {
    if (!previewUrl) return;
    // Named target → reuses the same tab on repeat clicks instead of piling up.
    const w = window.open(previewUrl, 'risma-preview-full');
    if (w) {
      popupRefs.current.add(w);
      w.focus();
    }
  }

  // ── first-load prefill ──
  // A client who has never edited should still open with every field filled
  // with the content Risma built the site from — not a blank form. The preview
  // hands us that base content; we adopt it (and persist it as the draft) the
  // first time, but ONLY when the DB draft was empty (so we never clobber real
  // edits a returning owner has saved).
  //
  // ECCEZIONE — sessione impersonata (staff dalla Console): un cliente nuovo,
  // prima del provisioning, non ha ancora un suo portale, quindi l'editor passa
  // dal portale OSPITE (oggi quello di Carlotta) e la sua anteprima è di un ALTRO
  // tenant. Adottarne il baseContent travaserebbe i contenuti di Carlotta nel
  // cliente nuovo. Quindi: impersonato → niente prefill, si parte puliti.
  const needPrefill = useRef(false);
  const baseContentRef = useRef<SiteContent | null>(null);
  function tryPrefill() {
    if (!needPrefill.current || !baseContentRef.current) return;
    needPrefill.current = false;
    const base = structuredClone(baseContentRef.current);
    setContent(base);
    contentRef.current = base;
    postPreview(base);
    // Persist once so the starting content is durable from day one.
    setStatus('saving');
    actions.saveSiteDraft(base).then(() => setStatus('saved')).catch(() => setStatus('error'));
  }

  useEffect(() => {
    let alive = true;
    actions
      .loadSite()
      .then((s) => {
        if (!alive) return;
        const draft = s.draft ?? {};
        setContent(draft);
        contentRef.current = draft;
        setPublished(!!s.publishedAt);
        setUploadsEnabled(!!s.uploadsEnabled);
        setStatus('saved');
        // Empty draft → flag for prefill; runs now if base content already
        // arrived from the preview, otherwise when its ready message lands.
        // Mai in impersonazione: l'anteprima ospite è di un altro tenant.
        if (Object.keys(draft).length === 0 && !isImpersonated) {
          needPrefill.current = true;
          tryPrefill();
        }
      })
      .catch(() => alive && setStatus('error'));
    return () => {
      alive = false;
      if (saveTimer.current) clearTimeout(saveTimer.current);
      if (previewTimer.current) clearTimeout(previewTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When a preview surface (iframe OR full-tab) signals it's ready, send the
  // current content straight back to that specific window so it syncs at once.
  useEffect(() => {
    const onMsg = (e: MessageEvent) => {
      if (!siteBase || e.origin !== siteBase || e.data?.source !== 'risma-preview') return;
      if (e.data.ready) {
        // Capture the base content (first surface to report wins) and prefill
        // if the editor was waiting on it.
        if (e.data.baseContent && !baseContentRef.current) {
          baseContentRef.current = e.data.baseContent as SiteContent;
          tryPrefill();
        }
        postTo((e.source as Window) ?? iframeRef.current?.contentWindow, contentRef.current);
      }
      // Editing sulla pagina: doppio-click su un'area dell'anteprima → l'editor
      // apre quella sezione (scroll + lampeggio + cursore nel primo campo).
      if (e.data.edit) {
        jump(e.data.edit);
        const card = document.getElementById('we-sec-' + e.data.edit);
        if (card) {
          card.classList.add('we-flash');
          setTimeout(() => card.classList.remove('we-flash'), 1400);
          setTimeout(() => (card.querySelector('input, textarea, select') as HTMLElement | null)?.focus(), 420);
        }
      }
      // Editing INLINE: testo modificato sulla pagina → scrivilo nella bozza (per
      // percorso-campo) e lascia che edit() salvi. Ricordo la finestra sorgente
      // così schedulePreview NON le rimanda il contenuto (anti-eco) per ~600ms.
      if (typeof e.data.field === 'string') {
        editingSourceRef.current = (e.source as Window | null) ?? null;
        if (editingClearTimer.current) clearTimeout(editingClearTimer.current);
        editingClearTimer.current = setTimeout(() => { editingSourceRef.current = null; }, 600);
        const v = coerce(e.data.value, e.data.kind);
        if (v !== SKIP) edit((c) => setByPath(c, e.data.field, v as string | number | undefined));
      }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Scroll-spy: highlight the section nearest the top of the scroll area.
  useEffect(() => {
    if (status === 'loading') return;
    const els = SECTIONS.map((s) => document.getElementById('we-sec-' + s.id)).filter(Boolean) as HTMLElement[];
    const io = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (visible) setActive(visible.target.id.replace('we-sec-', ''));
      },
      { rootMargin: '-80px 0px -65% 0px', threshold: 0 },
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [status]);

  function edit(producer: (c: SiteContent) => void) {
    setContent((prev) => {
      const next: SiteContent = structuredClone(prev);
      producer(next);
      scheduleSave(next);
      schedulePreview(next);
      return next;
    });
  }
  function scheduleSave(next: SiteContent) {
    setStatus('dirty');
    if (saveTimer.current) clearTimeout(saveTimer.current);
    // Durante la traduzione l'autosave è sospeso: il merge finale dell'overlay
    // salverà tutto insieme (testi correnti + locales.en), senza corse.
    if (translatingRef.current) return;
    saveTimer.current = setTimeout(() => {
      setStatus('saving');
      actions.saveSiteDraft(next).then(() => setStatus('saved')).catch(() => setStatus('error'));
    }, 800);
  }
  async function publish() {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    setStatus('saving');
    try {
      await actions.saveSiteDraft(content);
      setStatus('publishing');
      await actions.publishSite();
      setPublished(true);
      setStatus('published');
      setTimeout(() => setStatus('saved'), 2500);
    } catch {
      setStatus('error');
    }
  }
  function jump(id: string) {
    document.getElementById('we-sec-' + id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
  // Traduzione automatica (sezione "Lingue"): salva il draft corrente, chiede la
  // traduzione a Claude, poi porta l'overlay generato nello stato (il server l'ha
  // già scritto nel draft). I nomi dei piatti restano in italiano (lato server).
  async function runTranslate() {
    if (translating) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    translatingRef.current = true; // sospendi l'autosave finché non uniamo l'overlay
    setTranslating(true);
    setTranslateMsg(null);
    try {
      await actions.saveSiteDraft(content); // traduci i testi PIÙ RECENTI
      const overlay = await actions.translateSite();
      translatingRef.current = false; // riabilita: il merge sotto salva tutto
      edit((c) => { (c.locales ??= {}).en = overlay; });
      setTranslateMsg({ ok: true, text: 'Versione inglese generata. Rivedila qui sotto, poi attiva il bilingue e pubblica.' });
    } catch (err) {
      const status503 = (err as { status?: number })?.status === 503;
      const unavailable = status503 || (err instanceof Error && err.message.includes('unavailable'));
      setTranslateMsg({
        ok: false,
        text: unavailable
          ? 'Traduzione automatica non ancora attiva sul server (manca la chiave). Avvisa Risma.'
          : 'Traduzione non riuscita. Riprova tra poco.',
      });
    } finally {
      setTranslating(false);
      translatingRef.current = false; // riabilita sempre l'autosave (anche su errore)
    }
  }

  const id = content.identity ?? {};
  const hero = content.hero ?? {};
  const menu = content.menu ?? { lists: [] };
  const hours = content.hours ?? { timezone: 'Europe/Rome', week: [] };
  const gallery = content.gallery ?? { images: [] };
  const reviews = content.reviews ?? {};
  const contact = content.contact ?? {};
  const social = content.social ?? {};
  const seo = content.seo ?? {};
  const hoursDay = (day: SiteWeekday) => hours.week.find((d) => d.day === day) ?? { day, ranges: [] as { open: string; close: string }[] };

  const statusLabel: Record<Status, string> = {
    loading: 'Caricamento…',
    saved: 'Salvato',
    dirty: 'Modifiche non salvate',
    saving: 'Salvataggio…',
    error: 'Errore',
    publishing: 'Pubblicazione…',
    published: 'Pubblicato',
  };

  if (status === 'loading') {
    return <div className="view we-view"><p className="we-muted" style={{ padding: 24 }}>Caricamento del sito…</p></div>;
  }

  return (
    <div className="view we-view">
      {/* ── slim action bar (title lives in the topbar) ── */}
      <div className="we-head">
        <div className="we-head-l">
          {published ? <span className="we-live">● Online</span> : <span className="we-draft">● Bozza</span>}
        </div>
        <div className="we-head-r">
          <span className={'we-savechip we-savechip--' + status}>{statusLabel[status]}</span>
          <button className="we-btn we-btn-primary" onClick={() => void publish()} disabled={status === 'publishing' || status === 'saving'}>
            Pubblica
          </button>
        </div>
      </div>

      <div className={'we-shell' + (previewUrl ? ' we-shell--preview' : '')}>
        {/* ── section nav ── */}
        <nav className="we-nav" aria-label="Sezioni">
          <p className="we-nav-title">Sezioni</p>
          {SECTIONS.filter((s) => !hiddenSecs.has(s.id)).map((s) => (
            <button key={s.id} title={s.label} className={'we-nav-item' + (active === s.id ? ' active' : '')} onClick={() => jump(s.id)}>
              <SecIcon name={s.icon} /> <span>{s.label}</span>
            </button>
          ))}
        </nav>

        {/* ── content ── */}
        <div className="we-main">
          <Section id="hero" icon="star" title="Testata" desc={isLampo ? "L'hero kinetico di Lampo: titolone, foto che scorrono, claim." : isCarta ? "L'hero editoriale di Carta: foto incorniciata, titolone, frase, cerchio Prenota." : isMarea ? "L'hero immersivo di Marea: foto a tutto schermo che si alternano, marchio centrato, claim in corsivo." : "La prima cosa che vede chi arriva sul sito."}>
            <Field label={isLampo ? "Claim del manifesto" : isCarta ? "Frase sotto al nome" : "Frase sotto al nome"} hint={isLampo ? "La frase grande sotto l'hero — entra anche nel nastro che scorre." : isCarta ? "La frase grassettata sotto al nome, nell'hero." : "La frase che appare sotto al nome del locale."}>
              <textarea className="we-input we-textarea" rows={2} value={hero.subhead ?? ''} placeholder={isLampo ? "es. Solo carne, solo fuoco" : isCarta ? "es. La braceria nel cuore di Bologna" : "La frase che racconta il tuo locale…"}
                onChange={(e) => edit((c) => { (c.hero ??= {}).subhead = e.target.value || undefined; })} />
            </Field>
            {(isLampo || isMarea || heroSlideshow) ? (
              <Field label="Foto dell'hero" hint="Le foto grandi dell'hero — SEPARATE dalla Galleria. Con 2+ foto si alternano in dissolvenza a tutta pagina.">
                {(hero.images ?? []).length === 0 && <p className="we-empty">Nessuna foto. Carica 2-3 foto orizzontali: si alterneranno nella testata.</p>}
                {(hero.images ?? []).map((img, hi) => (
                  <GalleryRow key={hi} img={img} canUpload={uploadsEnabled} upload={uploadPhoto}
                    onSrc={(src) => edit((c) => { ((c.hero ??= {}).images ??= [])[hi].src = src; })}
                    onAlt={(alt) => edit((c) => { ((c.hero ??= {}).images ??= [])[hi].alt = alt; })}
                    onRemove={() => edit((c) => { c.hero!.images!.splice(hi, 1); })} />
                ))}
                <button type="button" className="we-link-add" onClick={() => edit((c) => { ((c.hero ??= {}).images ??= []).push({ src: '', alt: '' }); })}>+ Aggiungi foto</button>
              </Field>
            ) : (
              <ImageField label={isCarta ? "Foto dell'hero" : "Foto della testata"} hint={isCarta ? "La foto incorniciata a sinistra dell'hero." : "La grande foto in cima alla pagina."} img={hero.image}
                canUpload={uploadsEnabled} upload={uploadPhoto}
                onChange={(img) => edit((c) => { (c.hero ??= {}).image = img; })} />
            )}
          </Section>

          <Section id="identita" icon="id" title="Identità" desc="Il logo, il nome e dove siete.">
            <ImageField label="Logo" hint="Mostrato in alto e nel footer. Se manca, si usa il nome scritto. Meglio un PNG o SVG con sfondo trasparente." img={id.logo}
              canUpload={uploadsEnabled} upload={uploadPhoto}
              onChange={(img) => edit((c) => { (c.identity ??= {}).logo = img; })} />
            {id.logo?.src && (
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, margin: '2px 0 8px', cursor: 'pointer' }}>
                <input type="checkbox" style={{ marginTop: 3 }} checked={!!id.logoHasName}
                  onChange={(e) => edit((c) => { (c.identity ??= {}).logoHasName = e.target.checked || undefined; })} />
                <span>Il logo contiene già il nome del locale{' '}
                  <span style={{ opacity: 0.6 }}>(non mostrarlo anche scritto di fianco nella barra)</span></span>
              </label>
            )}
            <Field label="Slogan (tagline)" hint="Una frase breve di benvenuto.">
              <input className="we-input" value={id.tagline ?? ''} placeholder="La tua frase di benvenuto"
                onChange={(e) => edit((c) => { (c.identity ??= {}).tagline = e.target.value || undefined; })} />
            </Field>
            <Row>
              <Field label="Anno di fondazione">
                <input className="we-input" type="number" value={id.foundedYear ?? ''} placeholder="es. 1985"
                  onChange={(e) => edit((c) => { (c.identity ??= {}).foundedYear = e.target.value ? Number(e.target.value) : undefined; })} />
              </Field>
              <Field label="Zona / quartiere">
                <input className="we-input" value={id.locality ?? ''} placeholder="Il tuo quartiere o zona"
                  onChange={(e) => edit((c) => { (c.identity ??= {}).locality = e.target.value || undefined; })} />
              </Field>
            </Row>
          </Section>

          <Section id="racconto" icon="book" title="Racconto" desc="La sezione che racconta il locale — o &quot;Chi siamo&quot;." hidden={hiddenSecs.has('racconto')}>
            <Field label="Etichetta della sezione" hint="Il titolino sopra al testo (default: Storia). Se non avete una 'storia', rinominatela — es. 'Chi siamo', 'La cucina', 'Il territorio'.">
              <input className="we-input" value={id.storyLabel ?? ''} placeholder="Storia"
                onChange={(e) => edit((c) => { (c.identity ??= {}).storyLabel = e.target.value || undefined; })} />
            </Field>
            <Row>
              <Field label="Titolo">
                <input className="we-input" value={id.storyHeading ?? ''} placeholder="Il titolo del racconto"
                  onChange={(e) => edit((c) => { (c.identity ??= {}).storyHeading = e.target.value || undefined; })} />
              </Field>
              <Field label="Parola in evidenza" hint="Mostrata in corsivo/colore.">
                <input className="we-input" value={id.storyEmphasis ?? ''} placeholder="parola in evidenza"
                  onChange={(e) => edit((c) => { (c.identity ??= {}).storyEmphasis = e.target.value || undefined; })} />
              </Field>
            </Row>
            <Field label="Paragrafi">
              {(id.story ?? []).map((p, i) => (
                <div className="we-listrow" key={i}>
                  <span className="we-grip" aria-hidden="true">⠿</span>
                  <textarea className="we-input we-textarea" value={p} rows={2}
                    onChange={(e) => edit((c) => { (c.identity ??= {}).story![i] = e.target.value; })} />
                  <button className="we-link-rm" onClick={() => edit((c) => { c.identity!.story!.splice(i, 1); if (!c.identity!.story!.length) delete c.identity!.story; })}>Rimuovi</button>
                </div>
              ))}
              <button className="we-addline" onClick={() => edit((c) => { ((c.identity ??= {}).story ??= []).push(''); })}>+ Aggiungi paragrafo</button>
            </Field>
            <Field label="Citazione" hint="Mostrata solo se non c'è una foto qui sotto.">
              <input className="we-input" value={id.storyPull ?? ''} placeholder="Una frase che vi rappresenta."
                onChange={(e) => edit((c) => { (c.identity ??= {}).storyPull = e.target.value || undefined; })} />
            </Field>
            <ImageField label="Foto del racconto" img={id.storyImage}
              canUpload={uploadsEnabled} upload={uploadPhoto}
              onChange={(img) => edit((c) => { (c.identity ??= {}).storyImage = img; })} />
          </Section>

          <Section id="menu" icon="grid" title="Menù" desc="Una o più carte, con categorie e piatti."
            action={<button className="we-link-add" onClick={() => edit((c) => { (c.menu ??= { lists: [] }).lists.push({ name: 'Menù', categories: [] }); })}>+ Aggiungi carta</button>}>
            {menu.lists.length === 0 && <p className="we-empty">Nessuna carta. Aggiungine una per iniziare.</p>}
            {menu.lists.map((list, li) => (
              <div className="we-block" key={li}>
                <div className="we-block-head">
                  <span className="we-grip" aria-hidden="true">⠿</span>
                  <input className="we-input we-strong" value={list.name} placeholder="Alla carta"
                    onChange={(e) => edit((c) => { c.menu!.lists[li].name = e.target.value; })} />
                  <button className="we-link-rm" onClick={() => edit((c) => { c.menu!.lists.splice(li, 1); })}>Rimuovi</button>
                </div>
                <input className="we-input we-sub" value={list.description ?? ''} placeholder="Descrizione (facoltativa)"
                  onChange={(e) => edit((c) => { c.menu!.lists[li].description = e.target.value || undefined; })} />
                {(list.categories ?? []).map((cat, ci) => (
                  <div className="we-block we-block--cat" key={ci}>
                    <div className="we-block-head">
                      <input className="we-input we-cat" value={cat.name} placeholder="Antipasti"
                        onChange={(e) => edit((c) => { c.menu!.lists[li].categories![ci].name = e.target.value; })} />
                      <button className="we-link-rm" onClick={() => edit((c) => { c.menu!.lists[li].categories!.splice(ci, 1); })}>Rimuovi</button>
                    </div>
                    {cat.items.map((item, ii) => (
                      <div className="we-dish" key={ii}>
                        <div className="we-dish-top">
                          <input className="we-input" value={item.name} placeholder="Nome del piatto"
                            onChange={(e) => edit((c) => { c.menu!.lists[li].categories![ci].items[ii].name = e.target.value; })} />
                          <div className="we-price-wrap"><span className="we-eur">€</span>
                            <input className="we-input we-price" type="number" value={item.price ?? ''} placeholder="0"
                              onChange={(e) => edit((c) => { c.menu!.lists[li].categories![ci].items[ii].price = e.target.value ? Number(e.target.value) : undefined; })} /></div>
                          <button className="we-icon-rm" title="Rimuovi piatto" onClick={() => edit((c) => { c.menu!.lists[li].categories![ci].items.splice(ii, 1); })}>✕</button>
                        </div>
                        <input className="we-input we-sub" value={item.description ?? ''} placeholder="Descrizione"
                          onChange={(e) => edit((c) => { c.menu!.lists[li].categories![ci].items[ii].description = e.target.value || undefined; })} />
                        <DishPhoto img={item.image} canUpload={uploadsEnabled} upload={uploadPhoto}
                          onChange={(img) => edit((c) => { c.menu!.lists[li].categories![ci].items[ii].image = img; })} />
                        <div className="we-tags">
                          {TAGS.map((tg) => {
                            const on = (item.tags ?? []).includes(tg.key);
                            return (
                              <button key={tg.key} className={'we-chip' + (on ? ' on' : '')}
                                onClick={() => edit((c) => {
                                  const it = c.menu!.lists[li].categories![ci].items[ii];
                                  const set = new Set(it.tags ?? []);
                                  if (set.has(tg.key)) set.delete(tg.key); else set.add(tg.key);
                                  it.tags = set.size ? [...set] : undefined;
                                })}>{tg.label}</button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                    <button className="we-addline" onClick={() => edit((c) => { c.menu!.lists[li].categories![ci].items.push({ name: '' }); })}>+ Piatto</button>
                  </div>
                ))}
                <button className="we-addline" onClick={() => edit((c) => { (c.menu!.lists[li].categories ??= []).push({ name: '', items: [] }); })}>+ Categoria</button>
              </div>
            ))}
            <Field label="Nota a piè di menù">
              <input className="we-input" value={menu.note ?? ''} placeholder="es. Coperto €2 · menù del giorno"
                onChange={(e) => edit((c) => { (c.menu ??= { lists: [] }).note = e.target.value || undefined; })} />
            </Field>
          </Section>

          <Section id="orari" icon="clock" title="Orari" desc="Apertura per ogni giorno. Lascia chiuso dove serve.">
            <div className="we-hours">
              {DAYS.map((d) => {
                const day = hoursDay(d.key);
                return (
                  <div className="we-day" key={d.key}>
                    <span className="we-day-name">{d.label}</span>
                    <label className="we-closed">
                      <input type="checkbox" checked={!!day.closed}
                        onChange={(e) => edit((c) => {
                          const h = (c.hours ??= { timezone: 'Europe/Rome', week: [] });
                          let row = h.week.find((x) => x.day === d.key);
                          if (!row) { row = { day: d.key }; h.week.push(row); }
                          row.closed = e.target.checked || undefined;
                          if (e.target.checked) row.ranges = undefined;
                        })} /> Chiuso
                    </label>
                    {!day.closed && (
                      <div className="we-ranges">
                        {(day.ranges ?? []).map((r, ri) => (
                          <span className="we-range" key={ri}>
                            <input className="we-input we-time" value={r.open} placeholder="12:00"
                              onChange={(e) => edit((c) => { ensureDay(c, d.key).ranges![ri].open = e.target.value; })} />
                            <span className="we-dash">–</span>
                            <input className="we-input we-time" value={r.close} placeholder="15:00"
                              onChange={(e) => edit((c) => { ensureDay(c, d.key).ranges![ri].close = e.target.value; })} />
                            <button className="we-icon-rm" onClick={() => edit((c) => { ensureDay(c, d.key).ranges!.splice(ri, 1); })}>✕</button>
                          </span>
                        ))}
                        <button className="we-addline" onClick={() => edit((c) => { const row = ensureDay(c, d.key); (row.ranges ??= []).push({ open: '19:00', close: '23:00' }); row.closed = undefined; })}>+ Fascia</button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <Field label="Nota orari">
              <input className="we-input" value={hours.specialNote ?? ''} placeholder="Chiuso dal 10 al 24 agosto."
                onChange={(e) => edit((c) => { (c.hours ??= { timezone: 'Europe/Rome', week: [] }).specialNote = e.target.value || undefined; })} />
            </Field>
          </Section>

          <Section id="galleria" icon="image" title="Galleria" desc={isLampo ? "Le \"foto storte\" a ventaglio in fondo alla pagina." : "Le foto in fondo alla pagina."}
            action={<button className="we-link-add" onClick={() => edit((c) => { (c.gallery ??= { images: [] }).images.push({ src: '', alt: '' }); })}>+ Aggiungi foto</button>}>
            {gallery.images.length === 0 && <p className="we-empty">Nessuna foto.</p>}
            {gallery.images.map((img, gi) => (
              <GalleryRow key={gi} img={img} canUpload={uploadsEnabled} upload={uploadPhoto}
                onSrc={(src) => edit((c) => { c.gallery!.images[gi].src = src; })}
                onAlt={(alt) => edit((c) => { c.gallery!.images[gi].alt = alt; })}
                onRemove={() => edit((c) => { c.gallery!.images.splice(gi, 1); })} />
            ))}
          </Section>

          <Section id="recensioni" icon="message" title="Recensioni" desc="Cosa dicono i clienti — appare prima di Prenota. Se vuota, sparisce. Usa recensioni vere."
            action={<button className="we-link-add" onClick={() => edit((c) => { ((c.reviews ??= {}).items ??= []).push({ author: '', text: '' }); })}>+ Aggiungi recensione</button>} hidden={hiddenSecs.has('recensioni')}>
            <Row>
              <Field label="Voto medio" hint="Facoltativo, da 0 a 5 (es. 4.8).">
                <input className="we-input" type="number" step="0.1" min="0" max="5" value={reviews.overall ?? ''} placeholder="es. 4.8"
                  onChange={(e) => edit((c) => { (c.reviews ??= {}).overall = e.target.value ? Number(e.target.value) : undefined; })} />
              </Field>
              <Field label="Fonte del voto" hint="Es. Google, Tripadvisor.">
                <input className="we-input" value={reviews.overallSource ?? ''} placeholder="Google"
                  onChange={(e) => edit((c) => { (c.reviews ??= {}).overallSource = e.target.value || undefined; })} />
              </Field>
            </Row>
            {(reviews.items ?? []).length === 0 && <p className="we-empty">Nessuna recensione.</p>}
            {(reviews.items ?? []).map((rev, ri) => (
              <div className="we-block" key={ri}>
                <div className="we-block-head">
                  <input className="we-input we-strong" value={rev.author} placeholder="Nome cliente (es. Maria R.)"
                    onChange={(e) => edit((c) => { c.reviews!.items![ri].author = e.target.value; })} />
                  <button className="we-link-rm" onClick={() => edit((c) => { c.reviews!.items!.splice(ri, 1); if (!c.reviews!.items!.length) delete c.reviews!.items; })}>Rimuovi</button>
                </div>
                <textarea className="we-input we-textarea" rows={2} value={rev.text} placeholder="La recensione del cliente…"
                  onChange={(e) => edit((c) => { c.reviews!.items![ri].text = e.target.value; })} />
                <Field label="Stelle (facoltativo)">
                  <select className="we-input" value={rev.rating ?? ''}
                    onChange={(e) => edit((c) => { c.reviews!.items![ri].rating = e.target.value ? Number(e.target.value) : undefined; })}>
                    <option value="">— nessuna —</option>
                    <option value="5">★★★★★ (5)</option>
                    <option value="4">★★★★ (4)</option>
                    <option value="3">★★★ (3)</option>
                    <option value="2">★★ (2)</option>
                    <option value="1">★ (1)</option>
                  </select>
                </Field>
              </div>
            ))}
          </Section>

          <Section id="contatti" icon="mail" title="Contatti" desc="Dove trovarvi e come prenotare.">
            <p className="we-grouplabel">Indirizzo</p>
            <Field label="Via e numero">
              <input className="we-input" value={contact.address?.street ?? ''} placeholder="Via e numero civico"
                onChange={(e) => edit((c) => { ((c.contact ??= {}).address ??= { street: '', city: '', postalCode: '' }).street = e.target.value; })} />
            </Field>
            <Row cols="addr">
              <Field label="Città"><input className="we-input" value={contact.address?.city ?? ''} placeholder="Città"
                onChange={(e) => edit((c) => { ((c.contact ??= {}).address ??= { street: '', city: '', postalCode: '' }).city = e.target.value; })} /></Field>
              <Field label="CAP"><input className="we-input" value={contact.address?.postalCode ?? ''} placeholder="CAP"
                onChange={(e) => edit((c) => { ((c.contact ??= {}).address ??= { street: '', city: '', postalCode: '' }).postalCode = e.target.value; })} /></Field>
              <Field label="Provincia"><input className="we-input" value={contact.address?.region ?? ''} placeholder="Prov."
                onChange={(e) => edit((c) => { ((c.contact ??= {}).address ??= { street: '', city: '', postalCode: '' }).region = e.target.value || undefined; })} /></Field>
            </Row>
            <Field label="Link indicazioni (Google Maps)">
              <input className="we-input" value={contact.map?.directionsUrl ?? ''} placeholder="https://maps.google.com/?q=…"
                onChange={(e) => edit((c) => { ((c.contact ??= {}).map ??= {}).directionsUrl = e.target.value || undefined; })} />
            </Field>
            <p className="we-grouplabel">Recapiti</p>
            <Row cols="3">
              <Field label="Telefono"><input className="we-input" value={contact.phone ?? ''} placeholder="+39 …"
                onChange={(e) => edit((c) => { (c.contact ??= {}).phone = e.target.value || undefined; })} /></Field>
              <Field label="Email"><input className="we-input" value={contact.email ?? ''} placeholder="tavoli@…"
                onChange={(e) => edit((c) => { (c.contact ??= {}).email = e.target.value || undefined; })} /></Field>
              <Field label="WhatsApp"><input className="we-input" value={contact.whatsapp ?? ''} placeholder="+39…"
                onChange={(e) => edit((c) => { (c.contact ??= {}).whatsapp = e.target.value || undefined; })} /></Field>
            </Row>
          </Section>

          <Section id="social" icon="share" title="Social" desc="Solo i profili che inserisci appaiono nel sito.">
            <Row>
              <Field label="Instagram"><input className="we-input" value={social.instagram ?? ''} placeholder="https://instagram.com/…"
                onChange={(e) => edit((c) => { (c.social ??= {}).instagram = e.target.value || undefined; })} /></Field>
              <Field label="Facebook"><input className="we-input" value={social.facebook ?? ''} placeholder="https://facebook.com/…"
                onChange={(e) => edit((c) => { (c.social ??= {}).facebook = e.target.value || undefined; })} /></Field>
            </Row>
            <Row>
              <Field label="TripAdvisor"><input className="we-input" value={social.tripadvisor ?? ''} placeholder="https://tripadvisor.com/…"
                onChange={(e) => edit((c) => { (c.social ??= {}).tripadvisor = e.target.value || undefined; })} /></Field>
              <Field label="Profilo Google Business"><input className="we-input" value={social.googleBusiness ?? ''} placeholder="https://g.page/…"
                onChange={(e) => edit((c) => { (c.social ??= {}).googleBusiness = e.target.value || undefined; })} /></Field>
            </Row>
          </Section>

          <Section id="seo" icon="search" title="Google & SEO" desc="Come appare il sito su Google e quando lo condividi.">
            <Field label="Titolo">
              <input className="we-input" value={seo.title ?? ''} placeholder="Nome del locale — cosa siete e dove"
                onChange={(e) => edit((c) => { (c.seo ??= {}).title = e.target.value || undefined; })} />
            </Field>
            <Field label="Descrizione" hint="~1-2 frasi. Appare sotto al titolo nei risultati di ricerca.">
              <textarea className="we-input we-textarea" rows={2} value={seo.description ?? ''} placeholder="Una frase che descrive il locale per Google…"
                onChange={(e) => edit((c) => { (c.seo ??= {}).description = e.target.value || undefined; })} />
            </Field>
          </Section>

          <Section id="lingue" icon="globe" title="Lingue" desc="Aggiungi una versione inglese del sito — utile nelle zone turistiche. Una bandierina IT/EN comparirà in alto.">
            <Field label="Traduzione automatica" hint="Claude traduce i testi in inglese. I NOMI DEI PIATTI restano in italiano. Rigenerala quando cambi i contenuti; poi rivedi e pubblica.">
              <button type="button" className="we-btn" disabled={translating || status === 'saving' || status === 'publishing'} onClick={() => void runTranslate()}>
                {translating ? 'Traduzione in corso…' : content.locales?.en ? '↻ Rigenera la versione inglese' : '🌐 Genera la versione inglese'}
              </button>
              {translateMsg && (
                <p style={{ marginTop: 8, fontSize: 13, color: translateMsg.ok ? 'var(--st-seated, #2e7d32)' : 'var(--st-noshow, #b3261e)' }}>
                  {translateMsg.text}
                </p>
              )}
              {content.locales?.en && !translateMsg && (
                <p className="we-hint" style={{ marginTop: 8 }}>
                  Hai cambiato testi o menù dopo aver tradotto? Premi <b>Rigenera</b> per aggiornare l'inglese, poi ripubblica.
                </p>
              )}
            </Field>

            {content.locales?.en && (
              <Field label="Anteprima inglese (generata)" hint="I testi tradotti principali. Anche il menù è tradotto (con i nomi dei piatti in italiano).">
                <div style={{ border: '1px solid var(--line, #e6e1d8)', borderRadius: 10, padding: '12px 14px', fontSize: 13, display: 'grid', gap: 6, color: 'var(--ink, #26241d)' }}>
                  {content.locales.en.identity?.tagline && <div><b>Tagline:</b> {content.locales.en.identity.tagline}</div>}
                  {content.locales.en.hero?.subhead && <div><b>Hero:</b> {content.locales.en.hero.subhead}</div>}
                  {content.locales.en.seo?.title && <div><b>SEO:</b> {content.locales.en.seo.title}</div>}
                  {(content.locales.en.identity?.story ?? []).map((p, i) => (
                    <div key={i} style={{ color: 'var(--mute, #7c7561)' }}>{p}</div>
                  ))}
                </div>
              </Field>
            )}

            <Field label="Sito bilingue (Italiano + Inglese)" hint="Attivalo quando la versione inglese è pronta: il sito pubblicherà anche /en con la bandierina IT/EN. Poi premi Pubblica.">
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: content.locales?.en ? 'pointer' : 'not-allowed', opacity: content.locales?.en ? 1 : 0.55 }}>
                <input type="checkbox" checked={(content.bilingual ?? false) && !!content.locales?.en}
                  disabled={!content.locales?.en}
                  onChange={(e) => edit((c) => { c.bilingual = e.target.checked || undefined; })} />
                <span>Pubblica anche la versione inglese {content.locales?.en ? '' : '— genera prima la traduzione qui sopra'}</span>
              </label>
            </Field>
          </Section>
        </div>

        {previewUrl && (
          <aside className="we-preview">
            <div className="we-preview-bar">
              <span>Anteprima dal vivo <span style={{ opacity: 0.6, fontWeight: 400 }}>· doppio-click su un'area per modificarla</span></span>
              <a
                href={previewUrl}
                target="risma-preview-full"
                onClick={(e) => {
                  e.preventDefault();
                  openFullPreview();
                }}
              >
                Apri ↗
              </a>
            </div>
            <div className="we-preview-stage">
              <iframe ref={iframeRef} src={previewUrl} title="Anteprima del sito" className="we-preview-frame" />
            </div>
          </aside>
        )}
      </div>
    </div>
  );
}

function ensureDay(c: SiteContent, day: SiteWeekday) {
  const h = (c.hours ??= { timezone: 'Europe/Rome', week: [] });
  let row = h.week.find((x) => x.day === day);
  if (!row) { row = { day, ranges: [] }; h.week.push(row); }
  row.ranges ??= [];
  return row;
}

// ── presentational helpers ──
function Section({ id, icon, title, desc, action, children, hidden }: {
  id: string; icon: SecName; title: string; desc?: string; action?: React.ReactNode; children: React.ReactNode; hidden?: boolean;
}) {
  if (hidden) return null;
  return (
    <section className="we-card" id={'we-sec-' + id}>
      <header className="we-card-head">
        <span className="we-card-ico"><SecIcon name={icon} /></span>
        <div className="we-card-head-t">
          <h3>{title}</h3>
          {desc && <p className="we-muted">{desc}</p>}
        </div>
        {action && <div className="we-card-action">{action}</div>}
      </header>
      <div className="we-card-body">{children}</div>
    </section>
  );
}
function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="we-field">
      <span className="we-label">{label}</span>
      {hint && <span className="we-hint">{hint}</span>}
      {children}
    </div>
  );
}
function Row({ children, cols }: { children: React.ReactNode; cols?: '3' | 'addr' }) {
  return <div className={'we-row' + (cols ? ' we-row--' + cols : '')}>{children}</div>;
}
// Shared file-picker logic: a hidden <input>, a busy flag, and an error flag.
// `onUrl` receives the uploaded photo's public URL.
function usePhotoPicker(upload: (f: File) => Promise<string>, onUrl: (url: string) => void) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(false);
  async function onFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file
    if (!file) return;
    setErr(false);
    setBusy(true);
    try {
      onUrl(await upload(file));
    } catch {
      setErr(true);
    } finally {
      setBusy(false);
    }
  }
  return { inputRef, busy, err, onFile, pick: () => inputRef.current?.click() };
}

const ACCEPT = 'image/jpeg,image/png,image/webp';

const PhotoIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="16" rx="2" /><circle cx="8.5" cy="9.5" r="1.5" /><path d="M21 16l-5-5L5 20" />
  </svg>
);

/** Foto compatta per singolo piatto (layout menu "Con foto", #39). */
function DishPhoto({ img, canUpload, upload, onChange }: {
  img?: { src: string; alt: string };
  canUpload: boolean;
  upload: (file: File) => Promise<string>;
  onChange: (img: { src: string; alt: string } | undefined) => void;
}) {
  const setSrc = (src: string) => onChange(src ? { src, alt: img?.alt ?? '' } : undefined);
  const { inputRef, busy, onFile, pick } = usePhotoPicker(upload, setSrc);
  if (!canUpload && !img?.src) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
      <button
        type="button"
        className={'we-thumb' + (canUpload ? ' we-thumb--btn' : '')}
        style={{ width: 46, height: 46, ...(img?.src ? { backgroundImage: `url(${img.src})` } : {}) }}
        onClick={canUpload ? pick : undefined}
        disabled={!canUpload || busy}
        title="Foto del piatto"
        aria-label="Foto del piatto"
      >
        {busy ? <span className="we-spin" aria-hidden="true" /> : !img?.src && <PhotoIcon />}
      </button>
      {canUpload && (
        <span style={{ display: 'inline-flex', gap: 10, alignItems: 'center', fontSize: 12.5 }}>
          <button type="button" className="we-btn we-btn-soft" onClick={pick} disabled={busy}>
            {busy ? 'Caricamento…' : img?.src ? 'Cambia foto' : 'Foto del piatto'}
          </button>
          {img?.src && !busy && <button type="button" className="we-link-rm" onClick={() => onChange(undefined)}>Rimuovi</button>}
        </span>
      )}
      <input ref={inputRef} type="file" accept={ACCEPT} hidden onChange={onFile} />
    </div>
  );
}

function ImageField({ label, hint, img, onChange, canUpload, upload }: {
  label: string; hint?: string; img?: { src: string; alt: string };
  onChange: (img: { src: string; alt: string } | undefined) => void;
  canUpload: boolean;
  upload: (file: File) => Promise<string>;
}) {
  const setSrc = (src: string) => onChange(src ? { src, alt: img?.alt ?? '' } : undefined);
  const { inputRef, busy, err, onFile, pick } = usePhotoPicker(upload, setSrc);
  return (
    <div className="we-field">
      <span className="we-label">{label}</span>
      {hint && <span className="we-hint">{hint}</span>}
      <div className="we-imgfield">
        <button
          type="button"
          className={'we-thumb' + (canUpload ? ' we-thumb--btn' : '')}
          style={img?.src ? { backgroundImage: `url(${img.src})` } : undefined}
          onClick={canUpload ? pick : undefined}
          disabled={!canUpload || busy}
          aria-label={canUpload ? 'Carica foto' : undefined}
        >
          {busy ? <span className="we-spin" aria-hidden="true" /> : !img?.src && <PhotoIcon />}
        </button>
        <div className="we-imgfield-inputs">
          {canUpload && (
            <div className="we-upload-row">
              <button type="button" className="we-btn we-btn-soft" onClick={pick} disabled={busy}>
                {busy ? 'Caricamento…' : img?.src ? 'Cambia foto' : 'Carica foto'}
              </button>
              {img?.src && !busy && (
                <button type="button" className="we-link-rm" onClick={() => onChange(undefined)}>Rimuovi</button>
              )}
              {err && <span className="we-upload-err">Caricamento non riuscito</span>}
              <input ref={inputRef} type="file" accept={ACCEPT} hidden onChange={onFile} />
            </div>
          )}
          <input className="we-input" value={img?.src ?? ''} placeholder={canUpload ? 'oppure incolla un link…' : 'https://…/foto.jpg'}
            onChange={(e) => setSrc(e.target.value)} />
          <input className="we-input" value={img?.alt ?? ''} placeholder="Descrizione foto (per accessibilità)"
            onChange={(e) => onChange(img?.src ? { src: img.src, alt: e.target.value } : undefined)} />
        </div>
      </div>
    </div>
  );
}

// One row of the gallery: thumbnail + URL/alt inputs + upload + remove.
function GalleryRow({ img, canUpload, upload, onSrc, onAlt, onRemove }: {
  img: { src: string; alt: string };
  canUpload: boolean;
  upload: (file: File) => Promise<string>;
  onSrc: (src: string) => void;
  onAlt: (alt: string) => void;
  onRemove: () => void;
}) {
  const { inputRef, busy, err, onFile, pick } = usePhotoPicker(upload, onSrc);
  return (
    <div className="we-listrow we-galrow">
      <span className="we-galthumb" style={img.src ? { backgroundImage: `url(${img.src})` } : undefined} aria-hidden="true">
        {!img.src && !busy && <PhotoIcon />}
        {busy && <span className="we-spin" aria-hidden="true" />}
      </span>
      <div className="we-galrow-inputs">
        <input className="we-input" value={img.src} placeholder={canUpload ? 'Carica o incolla un link…' : 'https://…/foto.jpg'}
          onChange={(e) => onSrc(e.target.value)} />
        <input className="we-input" value={img.alt} placeholder="Descrizione"
          onChange={(e) => onAlt(e.target.value)} />
        {err && <span className="we-upload-err">Caricamento non riuscito</span>}
      </div>
      {canUpload && (
        <>
          <button type="button" className="we-btn we-btn-soft" onClick={pick} disabled={busy}>
            {busy ? '…' : img.src ? 'Cambia' : 'Carica'}
          </button>
          <input ref={inputRef} type="file" accept={ACCEPT} hidden onChange={onFile} />
        </>
      )}
      <button className="we-link-rm" onClick={onRemove}>Rimuovi</button>
    </div>
  );
}

// ── tiny section icon set ──
type SecName = 'star' | 'id' | 'book' | 'grid' | 'clock' | 'image' | 'message' | 'mail' | 'share' | 'search' | 'globe';
function SecIcon({ name }: { name: SecName }) {
  const P: Record<SecName, string> = {
    globe: '<circle cx="12" cy="12" r="8.5"/><path d="M3.5 12h17"/><path d="M12 3.5c2.4 2.3 3.7 5.3 3.7 8.5S14.4 18.2 12 20.5C9.6 18.2 8.3 15.2 8.3 12S9.6 5.8 12 3.5z"/>',
    star: '<path d="M12 3l2.6 5.6 6 .8-4.4 4.1 1.1 6L12 16.9 6.7 19.6l1.1-6L3.4 9.4l6-.8L12 3z"/>',
    id: '<circle cx="12" cy="8" r="3.4"/><path d="M5.5 20a6.5 6.5 0 0 1 13 0"/>',
    book: '<path d="M5 4h11a2 2 0 0 1 2 2v14H7a2 2 0 0 1-2-2V4z"/><path d="M5 16h13"/>',
    grid: '<rect x="4" y="4" width="7" height="7" rx="1"/><rect x="13" y="4" width="7" height="7" rx="1"/><rect x="4" y="13" width="7" height="7" rx="1"/><rect x="13" y="13" width="7" height="7" rx="1"/>',
    clock: '<circle cx="12" cy="12" r="8"/><path d="M12 8v4l3 2"/>',
    image: '<rect x="3" y="4" width="18" height="16" rx="2"/><circle cx="8.5" cy="9.5" r="1.5"/><path d="M21 16l-5-5L5 20"/>',
    message: '<path d="M5 5h14a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H9l-4 4V6a1 1 0 0 1 1-1z"/>',
    mail: '<rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 6 9-6"/>',
    share: '<circle cx="6" cy="12" r="2.5"/><circle cx="18" cy="6" r="2.5"/><circle cx="18" cy="18" r="2.5"/><path d="M8.2 10.8 15.8 7M8.2 13.2l7.6 3.8"/>',
    search: '<circle cx="11" cy="11" r="7"/><path d="m21 21-4.3-4.3"/>',
  };
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" dangerouslySetInnerHTML={{ __html: P[name] }} />
  );
}
